/**
 * laneGeometry — THE ONE DECLARATION of a node's ATTACHMENT LANE.
 *
 * Every node on the read-only canvas may carry satellites hanging below-right
 * of it in one lane: its model disc (`withModelSatellites`), the members it
 * CONSUMES (`uses:` → `withMemberSatellites`, connector `uses`) and the members
 * it STARTS (`dispatchesWorkflow` → the same module, connector `starts`). Each
 * is the same drawing — an icon with a caption, joined by a dashed line — and
 * they sit side by side at one pitch.
 *
 * Three consumers read this geometry, and they MUST agree or the picture lies:
 *
 *   • the two satellite PLACERS, which put seat `i` at `SAT_DX + i · pitch`;
 *   • the LAYOUTER (`layout.js nodeSize`), which has to reserve horizontal room
 *     for the lane BEFORE ELK runs, or the next layer's nodes land on top of
 *     the satellites (board-runner's `Start the workers` carries 1 consumed +
 *     7 started members: eight seats, ~750px of lane, under a next layer that
 *     used to start 306px to the right);
 *   • the CONVERTER (`graphs.js`), which stamps the static seat count on the
 *     node so the layouter can read it.
 *
 * Hence one module, one `lanePitch`, one `laneFootprint`, and a test that
 * asserts the placer's actual seat count equals the converter's stamp for the
 * same graph (TWO-PLACES rule).
 *
 * THE PITCH RULE. Up to three seats the lane keeps the pitch it has always had
 * (132px, a 110px caption under each 40px tile), so every existing graph lays
 * out byte-identically. From four seats it compacts to 84px with an 80px
 * caption — the same shape as the self-host dashboard's `lanePitch(k)` rule
 * (roomy alone, tighter as the lane fills), with the cloud's own numbers.
 *
 * The footprint is reserved ONLY where the compact rule applies: a lane of up
 * to three seats is exactly as wide as it was yesterday, and yesterday's layer
 * spacing is what it had.
 */
import { dispatchedSlugsOf } from '../../../utils/dispatchMarker';
import { nodeRunsModel } from '../../../utils/nodeModel';

// Where seat 0 sits relative to its node. Below-RIGHT: the node's own caption
// (124px wide, centered) owns the space directly beneath it, and the flow edge
// leaves the node's right side at icon-center height — well above this.
//
// The connector leaves the node's `bottom` handle, which CSS pins to the icon
// box's underside (top: 83px), NOT the bottom of the node element — so it
// starts at the box, above the caption.
export const SAT_DX = 104;
// The top of the lane. The edge renderer derives its ATTACHMENT_LANE from this
// so it never routes a line (or parks a label chip) across the satellites.
export const SAT_DY = 92;
// The icon tile (MemberNode.css / ModelNode.css: 40×40).
export const SAT_TILE = 40;

// Seat-to-seat distance, and the caption width that goes with it. The two
// pairs are read by MemberNode.css (`.gwe-member-node__caption` / its
// `is-compact` variant) — a tripwire in memberSatellites.test.js pins them.
export const SAT_STEP = 132;
export const SAT_CAPTION_W = 110;
export const SAT_STEP_COMPACT = 84;
export const SAT_CAPTION_W_COMPACT = 80;
// The seat count at which a lane switches to the compact pitch.
export const COMPACT_LANE_FROM = 4;

/**
 * Seat-to-seat pitch for a lane of `k` seats.
 * @param {number} k
 * @returns {number}
 */
export function lanePitch(k) {
  return Number(k) >= COMPACT_LANE_FROM ? SAT_STEP_COMPACT : SAT_STEP;
}

/** Whether a lane of `k` seats draws its satellites in the compact form. */
export function laneIsCompact(k) {
  return Number(k) >= COMPACT_LANE_FROM;
}

/**
 * Horizontal room a lane of `k` seats needs, measured from its node's LEFT
 * edge to the right edge of the last caption — or 0 when the lane is within
 * what the default node footprint + layer spacing already cover (k below the
 * compact threshold), so the layouter leaves those graphs untouched.
 *
 * @param {number} k
 * @returns {number}
 */
export function laneFootprint(k) {
  const n = Number(k) || 0;
  if (n < COMPACT_LANE_FROM) return 0;
  const captionOverhang = (SAT_CAPTION_W_COMPACT - SAT_TILE) / 2;
  return SAT_DX + (n - 1) * lanePitch(n) + SAT_TILE + captionOverhang;
}

/**
 * The aliases a node declares it consumes (`uses: ['kb']`), as the engine
 * serializes them (an array; a bare string is tolerated for hand-built data).
 * De-duplicated, because each alias is one satellite.
 *
 * @param {object|null|undefined} cfg  a node's serialized config
 * @returns {string[]}
 */
export function usesAliasesOf(cfg) {
  const raw = cfg?.uses;
  const list = Array.isArray(raw) ? raw : (typeof raw === 'string' ? [raw] : []);
  const out = [];
  for (const v of list) {
    const alias = typeof v === 'string' ? v.trim() : '';
    if (alias && !out.includes(alias)) out.push(alias);
  }
  return out;
}

/**
 * How many satellites a node's DECLARATION puts in its lane: one per started
 * member, one per consumed member, plus its model disc. Static — derived from
 * the serialized graph alone, before any row or credential is known — which is
 * what lets the layouter reserve the room up front.
 *
 * @param {object|null|undefined} cfg  a node's serialized config
 * @returns {number}
 */
export function laneSeatsOf(cfg) {
  if (!cfg || typeof cfg !== 'object') return 0;
  return dispatchedSlugsOf(cfg.dispatchesWorkflow).length
    + usesAliasesOf(cfg).length
    + (nodeRunsModel(cfg) ? 1 : 0);
}
